import React, {useEffect, useRef, useState} from 'react';
import Dialog from '../../../../../components/dialogs/Dialog';
import DialogTitle from '../../../../../components/dialogs/DialogTitle';
import DialogContent from '../../../../../components/dialogs/DialogContent';
import TextField from '../../../../../components/fields/textfield/TextField';
import Grid from '../../../../../components/dialogs/Grid';
import {t} from '../../../../../store/localization/TranslMessasge';
import {CommonTranslationKey} from '../../CommonTranslationKey';
import {ButtonLayout, NegativeButtons, PositiveButtons} from "../../../../../components/button/ButtonLayout";
import {Button} from "../../../../../components/button/Button";
import {CancelButton} from "../../../../../components/button/CancelButton";
import DialogActions from "../../../../../components/dialogs/DialogActions";
import Snackbar from "../../snackbar/Snackbar";
import {ErrorTranslationKey} from "../../ErrorTranslationKey";
import {
    AttributeDefinitionCreateDto,
    AttributePropertiesDto
} from '../../../../../common/apis/stereotype/AttributeDefinitionDto';
import {StereotypeApiClient} from "../../../../../common/apis/stereotype/StereotypeApiClient";
import {StereotypesTranslationKey} from "../StereotypesTranslationKey";
import {useQuery} from "@tanstack/react-query";
import {DataTypeApiClient} from "../../../../../common/apis/datatype/DataTypeApiClient";
import Checkbox from "../../../../../components/fields/Checkbox";
import SingleSelectField from "../../../../../components/fields/select/SingleSelectField";
import {ValidationError} from "../../../../../common/ValidationError";
import {StereotypeErrorCode} from "../StereotypeErrorCodes";
import {AttributePropertiesPlugin} from "./AttributePropertiesPlugin";
import {DataTypeDefinitionDto} from "../../../../../common/apis/datatype/DataTypeDefinitionDto";
import attributePropertiesPluginRegistry from "./AttributePropertiesPluginRegistryFactory";

interface AttributeDefinitionCreateDialogProps {
    open: boolean;
    stereotypeId: string;
    onCreated: (attributeDefinitionId: string) => void;
    onClose: () => void;
}

export function AttributeDefinitionCreateDialog({ open, stereotypeId, onCreated, onClose }: Readonly<AttributeDefinitionCreateDialogProps>) {
    const stereotypeApiClient = useRef(new StereotypeApiClient());
    const dataTypeApiClient = useRef(new DataTypeApiClient());

    const [attributeDefinition, setAttributeDefinition] = useState<Partial<AttributeDefinitionCreateDto>>({});
    const [dataTypeDefinition, setDataTypeDefinition] = useState<DataTypeDefinitionDto | undefined>();
    const [attrPropsPlugin, setAttrPropsPlugin] = useState<AttributePropertiesPlugin | undefined>(undefined);
    const [attributeProperties, setAttributeProperties] = useState<AttributePropertiesDto | undefined>();
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [inProgress, setInProgress] = useState(false);

    useEffect(() => {
        if (open) {
            setAttributeDefinition({
                name: "",
                description: "",
                dataTypeId: undefined,
                required: false,
                defaultValue: undefined
            });
            setDataTypeDefinition(undefined);
            setErrors({});
        }
    }, [open]);

    useEffect(() => {
        let plugin = undefined;
        if (dataTypeDefinition?.coreDataType) {
            plugin = attributePropertiesPluginRegistry.getPlugin(dataTypeDefinition?.coreDataType);
        }
        setAttrPropsPlugin(plugin);

        if (plugin) {
            const initialAttributeProperties = plugin.getInitialValue();
            setAttributeProperties(initialAttributeProperties);
        }
    }, [dataTypeDefinition]);

    const {data: dataTypes} = useQuery({
        queryKey: ["dataType.dataTypes"],
        queryFn: () => dataTypeApiClient.current.findAll(),
    });

    function onDataTypeChanged(dataType: DataTypeDefinitionDto | null | undefined) {
        setDataTypeDefinition(dataType ?? undefined);
        setAttributeDefinition({...attributeDefinition, dataTypeId: dataType?.id});
    }

    function createAttributeDefinition() {
        if (!validateForm()) {
            return;
        }

        setInProgress(true);

        const attributeDefinitionCreate: AttributeDefinitionCreateDto = {
            name: attributeDefinition.name!.trim(),
            description: attributeDefinition.description?.trim(),
            dataTypeId: attributeDefinition.dataTypeId!,
            required: attributeDefinition.required ?? false,
            defaultValue: attributeDefinition.defaultValue,
            additionalProperties: attributeProperties,
        };

        stereotypeApiClient.current.createAttribute(stereotypeId, attributeDefinitionCreate)
                .then((definitionId) => {
                    onCreated(definitionId);
                    onClose();
                    Snackbar.success(t(StereotypesTranslationKey.ATTRIBUTE_CREATED_SUCCESSFULLY));
                })
                .catch((error) => {
                    if (error instanceof ValidationError && error.error.code === StereotypeErrorCode.CONFLICTING_NAME) {
                        setErrors({name: t(StereotypesTranslationKey.ATTRIBUTE_WITH_SAME_NAME_ALREADY_EXISTS)});
                    } else {
                        Snackbar.error(t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED), error);
                    }
                })
                .finally(() => setInProgress(false));
    }

    function validateForm(): boolean {
        const newErrors: Record<string, string> = {};
        if (!attributeDefinition.name?.trim()) {
            newErrors.name = t(CommonTranslationKey.FILL_OUT_THIS_FIELD);
        }

        if (!attributeDefinition.dataTypeId) {
            newErrors.dataType = t(CommonTranslationKey.FILL_OUT_THIS_FIELD);
        }

        if (attrPropsPlugin && attributeProperties) {
            Object.assign(newErrors, attrPropsPlugin.validate(attributeProperties));
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    const dataTypeOptions = dataTypes ?? [];
    dataTypeOptions.sort((a, b) => a.name.localeCompare(b.name));

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md">
            <DialogTitle title={t(StereotypesTranslationKey.NEW_ATTRIBUTE)}
                    onDialogClosed={onClose} />
            <DialogContent>
                <Grid container>
                    <Grid item xs={12} sm={6}>
                        <TextField
                                label={t(StereotypesTranslationKey.NAME)}
                                value={attributeDefinition.name}
                                onChange={value => setAttributeDefinition({...attributeDefinition, name: value})}
                                errorMessage={errors.name}
                                required={true}/>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <SingleSelectField
                                label={t(StereotypesTranslationKey.DATA_TYPE)}
                                value={dataTypeDefinition}
                                onChange={value => onDataTypeChanged(value)}
                                options={dataTypeOptions}
                                getOptionLabel={(dataType) => dataType.name}
                                errorMessage={errors.dataType}
                                required={true}/>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                                label={t(CommonTranslationKey.DESCRIPTION)}
                                value={attributeDefinition.description ?? ''}
                                onChange={value => setAttributeDefinition({...attributeDefinition, description: value})}
                                multiline
                                rows={4}/>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                                label={t(StereotypesTranslationKey.DEFAULT_VALUE)}
                                value={attributeDefinition.defaultValue ?? ''}
                                onChange={value => setAttributeDefinition({...attributeDefinition, defaultValue: value})}/>
                    </Grid>
                    {attrPropsPlugin && attributeProperties &&
                            <Grid item xs={12}>
                                {
                                    attrPropsPlugin.renderForm({
                                        editMode: true,
                                        value: attributeProperties,
                                        onChange: (value) => setAttributeProperties(value),
                                        errors: errors
                                    })
                                }
                            </Grid>
                    }
                    <Grid item xs={12}>
                        <Checkbox
                            label={t(StereotypesTranslationKey.REQUIRED)}
                            checked={attributeDefinition.required}
                            onChange={value => setAttributeDefinition({...attributeDefinition, required: value})}/>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <ButtonLayout>
                    <PositiveButtons>
                        <Button label={t(CommonTranslationKey.CREATE)}
                                variant="contained"
                                onClick={createAttributeDefinition}
                                disabled={inProgress} />
                    </PositiveButtons>
                    <NegativeButtons>
                        <CancelButton onClick={onClose} disabled={inProgress} />
                    </NegativeButtons>
                </ButtonLayout>
            </DialogActions>
        </Dialog>
    );
}